import React, { useCallback, useMemo, useState, useEffect } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import { Option } from 'slim-select/dist/data.d';

import Equipment from '../../../../../core/types/model/Equipment';
import DeliveryZone from '../../../../../core/types/model/DeliveryZone';
import deliveryZoneColorMap from '../../../../../core/constants/deliveryZoneColorMap';
import Select from '../../../../../components/form/select/Select';
import Badge from '../../../../components/badge/Badge';
import { equipmentToOption } from '../../../helpers';
import { isMobileScreenSize } from '../../../../../core/utils/isMobileScreenSize';

interface Props {
  deliveryZones: DeliveryZone[];
  transportEquipment: Equipment[];
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  defaultDeliveryZoneId?: string | number;
  defaultTransportEquipmentId?: string | number;
}

const deliveryZoneToOption = (deliveryZone: DeliveryZone): Option => ({
  value: deliveryZone.id.toString(),
  text: deliveryZone.name,
  innerHTML: ReactDOMServer.renderToStaticMarkup(
    <Badge icon={deliveryZone.creationIndex} label={deliveryZone.name} color={deliveryZoneColorMap[deliveryZone.creationIndex]} />
  ),
  data: {
    transportHierarchyOrder: deliveryZone.transportHierarchyOrder
  }
});

const TransportDeliveryZone: React.FC<Props> = ({
  transportEquipment,
  deliveryZones,
  control,
  setValue,
  defaultDeliveryZoneId,
  defaultTransportEquipmentId
}) => {
  const { t } = useTranslation();

  const equipmentOptions = useMemo(() => transportEquipment.map(equipmentToOption), [transportEquipment]);
  const deliveryZoneOptions = useMemo(() => deliveryZones.map(deliveryZoneToOption), [deliveryZones]);
  const isMobileScreen = isMobileScreenSize();

  const [validDeliveryZones, setValidDeliveryZones] = useState<Option[]>([]);

  const handleTransportEquipmentChange = useCallback(
    ({ value }: Option) => {
      setValue('deliveryZoneId', defaultDeliveryZoneId || null, { shouldDirty: true, shouldValidate: false });

      const selectedEquipment = transportEquipment.find(({ id }) => id.toString() === value) as Equipment;
      if (!selectedEquipment) {
        setValidDeliveryZones([]);
      } else {
        const validZones = deliveryZoneOptions.filter(
          (zoneOption) => (zoneOption.data as any).transportHierarchyOrder >= selectedEquipment.hierarchyOrder
        );
        setValidDeliveryZones(validZones);
      }
    },

    [defaultDeliveryZoneId, deliveryZoneOptions, setValue, transportEquipment]
  );

  useEffect(() => {
    const defaultOptions = {
      value: defaultTransportEquipmentId?.toString(),
      text: deliveryZones.find((x) => x.id.toString() === defaultDeliveryZoneId?.toString())?.name || ''
    };
    handleTransportEquipmentChange(defaultOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`${!isMobileScreen && 'form-row'}`}>
      <Controller
        name="transportEquipmentId"
        control={control}
        render={({ field: { onChange, name } }) => (
          <Select
            id="transport-equipment"
            label={t('booking.transport')}
            showSearch={false}
            name={name}
            options={equipmentOptions}
            onChange={(option) => {
              if (Array.isArray(option)) {
                [option] = option; // eslint-disable-line no-param-reassign
              }
              onChange(option.value);
              handleTransportEquipmentChange(option);
            }}
            required
            addPlaceholderOption
            value={String(defaultTransportEquipmentId)}
          />
        )}
      />

      <Controller
        name="deliveryZoneId"
        control={control}
        render={({ field: { onChange, name } }) => (
          <Select
            id="delivery-zone"
            className="select--reduced-padding"
            label={t('booking.deliveryZone')}
            showSearch={false}
            name={name}
            options={validDeliveryZones}
            onChange={(option) => {
              if (Array.isArray(option)) {
                [option] = option; // eslint-disable-line no-param-reassign
              }
              onChange(option.value);
            }}
            required
            disabled={validDeliveryZones.length === 0 && !defaultDeliveryZoneId}
            addPlaceholderOption
            value={String(defaultDeliveryZoneId)}
          />
        )}
      />
    </div>
  );
};

export default TransportDeliveryZone;
